import Highcharts from "highcharts";

import { formatBigNumber, formatPercent, formatNumber } from "~/core/Number";
import { Sectors } from "~/core/stocks/sector";

export async function createMarketMapChart({ data, layoutDirection = "vertical" }) {
  Highcharts.seriesTypes.treemap.prototype.stocksSquarified = createStocks;

  Highcharts.seriesTypes.treemap.prototype.sectorsSquarified = createSectorsSquarified;

  return {
    series: [
      {
        name: "Тепловая карта акций",
        type: "treemap",
        layoutAlgorithm: "stocksSquarified",
        allowDrillToNode: true,
        animationLimit: 1000,
        levelIsConstant: false,
        layoutStartingDirection: layoutDirection,
        clip: false,
        levels: [
          {
            level: 1,
            layoutAlgorithm: "sectorsSquarified",
            overflow: "none",
            dataLabels: {
              inside: true,
              enabled: true,
              useHTML: true,
              allowOverlap: true,
              style: {
                textOutline: false,
                fontWeight: "normal",
                fontFamily: "'TT Commons', sans-serif",
                color: "#ffffff"
              },
              formatter() {
                return getLabelByLevel(this.point);
              }
            },
            borderWidth: 5,
            borderColor: "#fff"
          },
          {
            level: 2,
            dataLabels: {
              inside: true,
              enabled: true,
              useHTML: true,
              style: {
                textOutline: false,
                fontWeight: "normal",
                fontFamily: "'Lab Grotesque', sans-serif",
                color: "#ffffff"
              },
              formatter() {
                return getLabelByLevel(this.point);
              }
            },
            borderWidth: 1,
            borderColor: "#fff"
          }
        ],
        cursor: "pointer",
        borderColor: "#fff",
        data: createDataChart(data),
        breadcrumbs: {
          buttonSpacing: 5,
          buttonTheme: {
            style: {
              color: "#335cad",
              fontFamily: "'TT Commons', sans-serif",
              fontSize: "16px"
            },
            states: {
              hover: {
                fill: "transparent"
              }
            }
          }
        }
      }
    ],
    tooltip: {
      borderWidth: 0,
      backgroundColor: "rgba(255,255,255,0)",
      shadow: false,
      useHTML: true,
      style: {
        fontFamily: "'Lab Grotesque', sans-serif",
        fontSize: "12px"
      },
      formatter() {
        if (this.point?.parent) {
          return `
            <div class="market-map-tooltip">
                <div>${this.point.companyName} (${this.key})</div>
                <div class="font-16 text-bold">${formatNumber(this.point.prices.current.value, {
                  currency: this.point.currency,
                  fullPrecision: true
                })} (<span class="${getClassForValueChanged(this.point.percentChange)}">${formatPercent(
            this.point.percentChange * 100
          )}</span>)</div>
                <div>Объем: ${formatBigNumber(this.point.value, { currency: this.point.currency })}</div>
            </div>
        `;
        } else {
          return `
            <div class="market-map-tooltip">
                <div>Сектор: ${this.key}</div>
                <div>Объем: ${formatBigNumber(this.point.value, { currency: this.point.currency })}</div>
            </div>
        `;
        }
      }
    },
    chart: {
      spacing: [0, 0, 0, 0],
      margin: [0, 0, 0, 0]
    },

    credits: {
      enabled: false
    },
    title: {
      text: ""
    },
    updateArgs: [true, true]
  };
}

function getLabelByLevel(data) {
  const { level } = data.node;
  const { shapeArgs } = data;

  if (shapeArgs && level === 1) {
    const { width, height } = shapeArgs;

    return `
      <div style="width:${width - 5}px; height: ${height}px; overflow: hidden; text-overflow: ellipsis">
        <span class="market-map-parent-label" style="width:${width - 5}px;">${data.options.name}</span>
      </div>`;
  }

  if (shapeArgs && level === 2) {
    const { width, height } = shapeArgs;

    const classFont = getFontsBySquare(shapeArgs);

    const stylePercentage = isSmallHeightSquare(shapeArgs) ? "style='display: none;'" : "";

    const styleLabel = isSmallSquare(shapeArgs) ? "color: transparent;" : "";

    const ticker = data.options.name;

    const stockPath =
      data.options.origin === "ru" ? `/rustock/${ticker.toLowerCase()}` : `/stock/${ticker.toLowerCase()}`;

    return `
      <a class="market-map-label" href="${stockPath}" style="width:${
      width - 3
    }px; height: ${height}px; ${styleLabel}" target="_blank">
        <span class="${classFont.ticker}">${ticker}</span> 
        <span class="${classFont.percentage}" ${stylePercentage}>${formatPercent(
      data.options.percentChange * 100
    )}</span>
      </a>
    `;
  }
}

function getFontsBySquare({ width, height }) {
  let fonts = { ticker: "font-12", percentage: "font-10" };

  if (height > 50) {
    if (width > 50) {
      fonts = { ticker: "font-14", percentage: "font-12" };
    }

    if (width > 80) {
      fonts = { ticker: "font-16", percentage: "font-14" };
    }

    if (width > 120 && height > 70) {
      fonts = { ticker: "font-25", percentage: "font-20" };
    }

    if (width > 150 && height > 90) {
      fonts = { ticker: "font-35", percentage: "font-25" };
    }

    if (width > 200 && height > 90) {
      fonts = { ticker: "font-40", percentage: "font-30" };
    }
  }

  return fonts;
}

const isSmallHeightSquare = ({ height }) => {
  return height < 40;
};

const isSmallSquare = ({ height, width }) => {
  return width < 35 || height < 20;
};

function createDataChart(data) {
  let dataSeries = [];

  for (const key in data) {
    dataSeries.push({
      id: key,
      name: Sectors.getLabel(key),
      color: "transparent",
      currency: data[key]?.[0]?.currency
    });

    for (const stock of data[key]) {
      dataSeries.push({
        name: stock.ticker,
        value: stock.volume,
        prices: stock.prices,
        percentChange: stock.percentChange,
        companyName: stock.companyName,
        currency: stock.currency,
        parent: key,
        color: getColorColumn(stock.percentChange * 100),
        origin: stock.origin
      });
    }
  }

  return dataSeries;
}

function getColorColumn(percent) {
  if (percent <= -3.01) {
    return "rgb(242, 54, 69)";
  }

  if (getValueInRange(percent, -3, -1.51)) {
    return "rgb(242, 54, 69)";
  }

  if (getValueInRange(percent, -1.5, 0.01)) {
    return "rgb(247, 124, 128)";
  }

  if (getValueInRange(percent, 0.01, 1.5)) {
    return "rgb(66, 189, 127)";
  }

  if (getValueInRange(percent, 1.51, 3)) {
    return "rgb(8, 153, 80)";
  }
  if (percent >= 3.01) {
    return "rgb(5, 102, 54)";
  }

  return "rgb(193, 196, 205)";
}

function getValueInRange(value, min, max) {
  return (value - min) * (value - max) <= 0;
}

function getClassForValueChanged(number) {
  if (!number) return "text-grey";

  if (number > 0) {
    return "text-green";
  } else if (number < 0) {
    return "text-red";
  }
}

function createSectorsSquarified(parent, children) {
  const fPercent = 0;

  const resChildren = Highcharts.seriesTypes.treemap.prototype.squarified(parent, children);
  const newChildren = [];

  resChildren.forEach((child, index) => {
    if (child.height > fPercent) {
      newChildren.push(children[index]);
    } else {
      children[index]["visible"] = false;
      children[index]["val"] = 0;
      children[index]["childrenTotal"] = 0;

      newChildren.push(children[index]);
    }
  });

  return Highcharts.seriesTypes.treemap.prototype.squarified(parent, newChildren);
}

function createStocks(parent, children) {
  const fPercent = 3;

  parent.height -= fPercent;
  parent.y += fPercent;
  children = Highcharts.seriesTypes.treemap.prototype.squarified(parent, children);
  parent.height += fPercent;
  parent.y -= fPercent;

  return children;
}

